import { SecurityHostProvider } from './security/host';
import { downloadCsv } from './security/standalone-download';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Spinner } from '@/components/ui/shared';
import { api, getUser, getAuthToken, logout } from '@/lib/api';

// Lazy-loaded pages
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const EventsPage = lazy(() => import('@/pages/EventsPage'));
const SubscriptionsPage = lazy(() => import('@/pages/SubscriptionsPage'));
const DeliveriesPage = lazy(() => import('@/pages/DeliveriesPage'));
const AggregatesPage = lazy(() => import('@/pages/AggregatesPage'));
const CostsPage = lazy(() => import('@/pages/CostsPage'));
const ApiKeysPage = lazy(() => import('@/pages/ApiKeysPage'));
const AuditLogsPage = lazy(() => import('@/pages/AuditLogsPage'));
const HealthPage = lazy(() => import('@/pages/HealthPage'));
const WebhooksPage = lazy(() => import('@/pages/WebhooksPage'));
const WebhookDetailPage = lazy(() => import('@/pages/WebhookDetailPage'));
const GitHubAppsPage = lazy(() => import('@/pages/GitHubAppsPage'));
const SecurityDashboardPage = lazy(() => import('@/pages/SecurityDashboardPage'));
const SecurityFindingDeliveryPage = lazy(() => import('@/pages/SecurityFindingDeliveryPage'));
const DependabotAlertsPage = lazy(() => import('@/pages/DependabotAlertsPage'));
const DependabotAlertDetailPage = lazy(() => import('@/pages/DependabotAlertDetailPage'));
const CodeScanningAlertsPage = lazy(() => import('@/pages/CodeScanningAlertsPage'));
const SecretScanningAlertsPage = lazy(() => import('@/pages/SecretScanningAlertsPage'));
const SecurityAdvisoriesPage = lazy(() => import('@/pages/SecurityAdvisoriesPage'));
const RepositoriesSecurityPage = lazy(() => import('@/pages/RepositoriesSecurityPage'));
const RepositoryManagementPage = lazy(() => import('@/pages/RepositoryManagementPage'));
const RepositoryDetailPage = lazy(() => import('@/pages/RepositoryDetailPage'));
const HttpSubscribersPage = lazy(() => import('@/pages/HttpSubscribersPage'));
const HttpSubscriberDetailPage = lazy(() => import('@/pages/HttpSubscriberDetailPage'));
const NotificationRulesPage = lazy(() => import('@/pages/NotificationRulesPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const AlertTrendsPage = lazy(() => import('@/pages/AlertTrendsPage'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = getAuthToken();
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

const standaloneSecurityApi = { ...api, download: downloadCsv };

function StandaloneSecurityHost({ children }: { children: React.ReactNode }) {
  useLocation(); // Refresh browser identity after login/logout navigation.
  return <SecurityHostProvider host={{ api: standaloneSecurityApi, user: getUser() }}>{children}</SecurityHostProvider>;
}

export default function App() {
  return (
    <StandaloneSecurityHost>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/aggregates" element={<AggregatesPage />} />
          <Route path="/security" element={<SecurityDashboardPage />} />
          <Route path="/security/finding-delivery" element={<SecurityFindingDeliveryPage />} />
          <Route path="/security/dependabot" element={<DependabotAlertsPage />} />
          <Route path="/security/dependabot/:id" element={<DependabotAlertDetailPage />} />
          <Route path="/security/code-scanning" element={<CodeScanningAlertsPage />} />
          <Route path="/security/secrets" element={<SecretScanningAlertsPage />} />
          <Route path="/security/advisories" element={<SecurityAdvisoriesPage />} />
          <Route path="/security/repositories" element={<RepositoriesSecurityPage />} />
          
          {/* Redirects for backward compatibility */}
          <Route path="/security-advisories" element={<Navigate to="/security/advisories" replace />} />
          <Route path="/repositories-security" element={<Navigate to="/security/repositories" replace />} />
          <Route path="/repositories" element={<RepositoryManagementPage />} />
          <Route path="/repositories/:id" element={<RepositoryDetailPage />} />
          <Route path="/subscriptions" element={<SubscriptionsPage />} />
          <Route path="/http-subscribers" element={<HttpSubscribersPage />} />
          <Route path="/http-subscribers/:id" element={<HttpSubscriberDetailPage />} />
          <Route path="/notifications" element={<NotificationRulesPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/security/trends" element={<AlertTrendsPage />} />
          <Route path="/subscribers" element={<SubscriptionsPage />} />
          <Route path="/deliveries" element={<DeliveriesPage />} />
          <Route path="/costs" element={<CostsPage />} />
          <Route path="/keys" element={<ApiKeysPage />} />
          <Route path="/audit" element={<AuditLogsPage />} />
          <Route path="/webhooks" element={<WebhooksPage />} />
          <Route path="/webhooks/:id" element={<WebhookDetailPage />} />
          <Route path="/github-apps" element={<GitHubAppsPage />} />
          <Route path="/health" element={<HealthPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
    </StandaloneSecurityHost>
  );
}
